/**
 * Drives the candidate's whole session.
 *
 * The server is the only authority on state and timing: this renders whatever
 * snapshot it was last given, and every action round-trips. It never advances
 * the test on its own — a countdown reaching zero here asks the server what
 * happened, and the server decides.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import DeadEnd from '../../components/DeadEnd';
import Spinner from '../../components/Spinner';
import { ItemTimer, TelemetryCollector } from '../../lib/telemetry';
import BreakScreen from './BreakScreen';
import CodenameStep from './CodenameStep';
import ConsentStep from './ConsentStep';
import { CompletedScreen, ForfeitedScreen } from './EndScreens';
import InstructionsStep from './InstructionsStep';
import QuestionScreen from './QuestionScreen';
import ReadyStep from './ReadyStep';
import type { Snapshot } from './types';

export default function CandidateTest() {
  const { token = '' } = useParams();

  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [dead, setDead] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  /** serverNow − clientNow, so a skewed client clock still shows honest time. */
  const [clockOffsetMs, setClockOffsetMs] = useState(0);

  const collector = useMemo(() => new TelemetryCollector(token), [token]);
  const timer = useMemo(() => new ItemTimer(), []);
  const opened = useRef(false);

  // Once the test has ended the server purges the session, so every later
  // request 404s. That is expected, not a dead link — this stops those 404s
  // flipping the UI to "not available" after a perfectly normal submission.
  const terminal = useRef(false);

  const apply = useCallback((data: Snapshot | null) => {
    if (!data) return;
    setSnapshot(data);
    if (typeof data.serverNow === 'number') setClockOffsetMs(data.serverNow - Date.now());
  }, []);

  const post = useCallback(
    async (path: string, body: Record<string, unknown> = {}): Promise<unknown | null> => {
      try {
        const response = await fetch(path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, ...body }),
        });

        if (response.status === 404) {
          if (!terminal.current) setDead(true);
          return null;
        }
        return await response.json();
      } catch {
        // A transient network failure must not look like a forfeit. The
        // heartbeat and the reconnect grace are what decide that, server-side.
        return null;
      }
    },
    [token],
  );

  /** Move to a terminal screen without polling the session that no longer exists. */
  const finishLocally = useCallback((state: 'completed' | 'forfeited', codename?: string) => {
    terminal.current = true;
    setSnapshot((previous) =>
      previous ? { ...previous, state, codename: codename ?? previous.codename } : previous,
    );
  }, []);

  // ---- opening ----------------------------------------------------------
  //
  // A POST rather than a side effect of loading the page, so a mail-security
  // crawler fetching the URL cannot burn a candidate's single attempt.
  useEffect(() => {
    if (opened.current || !token) return;
    opened.current = true;
    void (async () => {
      apply((await post('/api/session/open')) as Snapshot | null);
    })();
  }, [post, apply, token]);

  // ---- telemetry --------------------------------------------------------
  useEffect(() => {
    collector.start();
    return () => {
      collector.stop();
      void collector.flush(true);
    };
  }, [collector]);

  useEffect(() => {
    collector.setContext({
      sectionId: snapshot?.section?.id,
      itemId: snapshot?.item?.view.id,
    });
  }, [collector, snapshot?.section?.id, snapshot?.item?.view.id]);

  // ---- presence ---------------------------------------------------------
  const state = snapshot?.state;
  const isRunning = state === 'running';

  const readState = useCallback(async () => {
    apply((await post('/api/session/state')) as Snapshot | null);
  }, [post, apply]);

  useEffect(() => {
    if (!isRunning) return;
    const interval = snapshot?.heartbeatIntervalMs ?? 10_000;

    const beat = async () => {
      const result = (await post('/api/session/heartbeat')) as
        | { state?: string; serverNow?: number }
        | null;
      if (!result) return;
      if (typeof result.serverNow === 'number') setClockOffsetMs(result.serverNow - Date.now());
      // The server may have advanced or ended the test while we were idle.
      if (result.state === 'completed' || result.state === 'forfeited') {
        await readState();
      }
    };

    const id = setInterval(() => void beat(), interval);
    return () => clearInterval(id);
  }, [isRunning, post, readState, snapshot?.heartbeatIntervalMs]);

  /**
   * Tell the server we are going, which starts the reconnect grace clock.
   *
   * This does **not** forfeit: an accidental refresh is survivable, and the
   * grace is what decides. A beacon rather than a fetch, because the page is on
   * its way out and a normal request would be cancelled.
   */
  useEffect(() => {
    if (!isRunning) return;

    const onPageHide = () => {
      void collector.flush(true);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          '/api/session/disconnect',
          new Blob([JSON.stringify({ token })], { type: 'application/json' }),
        );
      }
    };

    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [isRunning, collector, token]);

  /** A refresh mid-test is survivable, but it should not be casual. */
  useEffect(() => {
    if (!isRunning) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isRunning]);

  // ---- actions ----------------------------------------------------------

  const guard = useCallback(
    async <T,>(work: () => Promise<T>): Promise<T | undefined> => {
      if (busy) return undefined;
      setBusy(true);
      try {
        return await work();
      } finally {
        setBusy(false);
      }
    },
    [busy],
  );

  const chooseCodename = (codename: string) =>
    void guard(async () => {
      const result = (await post('/api/session/codename', { codename })) as
        | (Snapshot & { error?: string })
        | null;
      if (result && 'error' in result && result.error) {
        setNotice('That codename could not be used. Please pick another.');
        return;
      }
      apply(result as Snapshot | null);
    });

  const passGate = (gate: 'instructions' | 'declaration' | 'monitoring') =>
    void guard(async () => {
      apply((await post('/api/session/gate', { gate })) as Snapshot | null);
    });

  const start = () =>
    void guard(async () => {
      // The click is also the user gesture browsers require before granting
      // fullscreen, which is why the request lives here and not in an effect.
      try {
        await document.documentElement.requestFullscreen?.();
      } catch {
        // Refused fullscreen is recorded by telemetry and is not a blocker.
      }
      apply((await post('/api/session/start')) as Snapshot | null);
    });

  const answer = async (value: string) => {
    const item = snapshot?.item;
    const section = snapshot?.section;
    if (!item || !section) return;

    const timing = timer.snapshot(value.length);
    const result = (await post('/api/session/answer', {
      sectionId: section.id,
      itemId: item.view.id,
      value,
      timing,
    })) as
      | { accepted: boolean; reason?: string; state?: string; snapshot?: Snapshot }
      | null;

    if (!result) return;

    if (result.state === 'completed') {
      finishLocally('completed', snapshot?.codename);
      return;
    }

    if (!result.accepted) {
      setNotice(refusal(result.reason));
    } else {
      setNotice(null);
    }
    if (result.snapshot) apply(result.snapshot);
  };

  const advance = async () => {
    const result = (await post('/api/session/advance')) as
      | (Snapshot & { state?: string })
      | null;
    if (result?.state === 'completed') {
      finishLocally('completed', snapshot?.codename);
      return;
    }
    apply(result as Snapshot | null);
  };

  const finish = async () => {
    const codename = snapshot?.codename;
    terminal.current = true;
    const result = (await post('/api/session/submit')) as
      | { state?: string; codename?: string }
      | null;
    finishLocally('completed', result?.codename ?? codename);
  };

  const abandon = () =>
    void guard(async () => {
      const codename = snapshot?.codename;
      terminal.current = true;
      const result = (await post('/api/session/abandon')) as { codename?: string } | null;
      finishLocally('forfeited', result?.codename ?? codename);
    });

  const takeBreak = () =>
    void guard(async () => {
      apply((await post('/api/session/break', { action: 'start' })) as Snapshot | null);
    });

  const resume = () =>
    void guard(async () => {
      apply((await post('/api/session/break', { action: 'end' })) as Snapshot | null);
    });

  // ---- render -----------------------------------------------------------

  if (dead) return <DeadEnd />;
  if (!snapshot) return <Spinner full label="Opening your test" />;

  if (snapshot.state === 'completed') return <CompletedScreen codename={snapshot.codename} />;
  if (snapshot.state === 'forfeited' || snapshot.state === 'submitting') {
    return <ForfeitedScreen codename={snapshot.codename} />;
  }

  if (snapshot.state === 'codename') {
    return (
      <CodenameStep
        positionTitle={snapshot.positionTitle}
        options={snapshot.codenameOptions ?? []}
        onChoose={chooseCodename}
        busy={busy}
      />
    );
  }

  if (snapshot.state === 'instructions') {
    return (
      <InstructionsStep
        positionTitle={snapshot.positionTitle}
        codename={snapshot.codename}
        outline={snapshot.outline ?? []}
        reconnectGraceMs={snapshot.reconnectGraceMs}
        breakConfig={snapshot.breakConfig ?? null}
        onContinue={() => passGate('instructions')}
        busy={busy}
      />
    );
  }

  if (snapshot.state === 'declaration' || snapshot.state === 'monitoring') {
    return (
      <ConsentStep
        kind={snapshot.state}
        text={
          (snapshot.state === 'declaration'
            ? snapshot.declarationText
            : snapshot.monitoringText) ?? ''
        }
        onAccept={() => passGate(snapshot.state as 'declaration' | 'monitoring')}
        busy={busy}
      />
    );
  }

  if (snapshot.state === 'ready') {
    return (
      <ReadyStep
        codename={snapshot.codename}
        positionTitle={snapshot.positionTitle}
        onStart={start}
        busy={busy}
      />
    );
  }

  if (snapshot.break?.active) {
    return (
      <BreakScreen
        snapshot={snapshot}
        clockOffsetMs={clockOffsetMs}
        onResume={resume}
        busy={busy}
      />
    );
  }

  if (!snapshot.section || !snapshot.item) {
    // Running, but between two questions as far as this client knows. Ask.
    return <Spinner full label="Loading the next question" />;
  }

  return (
    <>
      <QuestionScreen
        snapshot={snapshot}
        clockOffsetMs={clockOffsetMs}
        onAnswer={answer}
        onAdvance={advance}
        onFinish={finish}
        onBreak={takeBreak}
        onDeadline={() => void readState()}
        timer={timer}
        busy={busy}
        notice={notice}
      />
      <EndTestLink onEnd={abandon} busy={busy} />
    </>
  );
}

/**
 * The explicit way out.
 *
 * Deliberately quiet and deliberately present: a candidate who wants to stop
 * should be able to, and one who forfeits by closing the tab has learned the
 * rule the expensive way.
 */
function EndTestLink({ onEnd, busy }: { onEnd: () => void; busy: boolean }) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(false), 6000);
    return () => clearTimeout(timer);
  }, [armed]);

  return (
    <div className="fixed bottom-4 right-4 z-20">
      {armed ? (
        <div className="flex items-center gap-2 rounded-lg border border-danger/40 bg-paper/95 px-3 py-2 shadow-lg backdrop-blur">
          <span className="text-xs text-ink/70">End the test now? This cannot be undone.</span>
          <button
            type="button"
            className="btn-danger px-3 py-1 text-xs"
            onClick={onEnd}
            disabled={busy}
          >
            End it
          </button>
          <button
            type="button"
            className="px-2 py-1 text-xs text-ink/50 hover:text-ink"
            onClick={() => setArmed(false)}
          >
            Keep going
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="rounded-lg border border-veil/10 bg-paper/80 px-3 py-1.5 text-xs text-ink/40 backdrop-blur hover:text-ink/70"
          onClick={() => setArmed(true)}
        >
          End the test
        </button>
      )}
    </div>
  );
}

/** Why the server refused an answer, in words a candidate can act on. */
function refusal(reason: string | undefined): string {
  switch (reason) {
    case 'expired':
      return 'That answer arrived after the deadline and was not saved.';
    case 'on_break':
      return 'The test is paused. Resume it before answering.';
    case 'not_current_item':
      return 'That is not the question on screen any more.';
    case 'wrong_section':
      return 'The test has moved on to the next section.';
    case 'not_running':
      return 'This test is no longer running.';
    default:
      return 'That answer could not be saved.';
  }
}
